import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Plus,
  Trash2,
  Type,
  Upload,
  FileType
} from "lucide-react";
import MainLayout from "@/components/MainLayout";

export default function CustomFonts() {
  const [fonts, setFonts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fontToDelete, setFontToDelete] = useState(null);

  // New font form state
  const [newFontName, setNewFontName] = useState("");
  const [newFontFamily, setNewFontFamily] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadFonts();
  }, []);

  const loadFonts = async () => {
    try {
      const response = await axios.get(`${API}/custom-fonts`);
      setFonts(response.data);
    } catch (error) {
      toast.error("Failed to load custom fonts");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['.ttf', '.otf', '.woff', '.woff2'];
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      
      if (!allowedTypes.includes(extension)) {
        toast.error("Only .ttf, .otf, .woff, .woff2 files are allowed");
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      
      setSelectedFile(file);
      // Auto-fill name from filename if empty
      if (!newFontName) {
        const nameFromFile = file.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, " ");
        setNewFontName(nameFromFile);
      }
      // Auto-fill font family if empty
      if (!newFontFamily) {
        const familyFromFile = file.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, "");
        setNewFontFamily(familyFromFile);
      }
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      toast.error("Please select a font file");
      return;
    }
    
    if (!newFontName.trim()) {
      toast.error("Please enter a font name");
      return;
    }
    
    if (!newFontFamily.trim()) {
      toast.error("Please enter a font family name");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("name", newFontName.trim());
      formData.append("font_family", newFontFamily.trim());
      
      await axios.post(`${API}/custom-fonts`, formData);
      toast.success("Font uploaded successfully");
      
      // Reset form
      setNewFontName("");
      setNewFontFamily("");
      setSelectedFile(null);
      
      // Reload fonts
      loadFonts();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to upload font";
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (fontId) => {
    setFontToDelete(fontId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!fontToDelete) return;
    
    try {
      await axios.delete(`${API}/custom-fonts/${fontToDelete}`);
      toast.success("Font deleted successfully");
      setDeleteDialogOpen(false);
      setFontToDelete(null);
      loadFonts();
    } catch (error) {
      toast.error("Failed to delete font");
      setDeleteDialogOpen(false);
      setFontToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  return (
    <MainLayout title="Custom Fonts" subtitle="Upload and manage custom fonts for your banners">
      <div className="max-w-4xl">
        {/* Upload Form */}
        <Card className="bg-[#12121a] border-white/5 mb-8">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Upload New Font
              </h2>
              
              <form onSubmit={handleUpload} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-400">Font Name</Label>
                    <Input
                      value={newFontName}
                      onChange={(e) => setNewFontName(e.target.value)}
                      placeholder="My Custom Font"
                      className="mt-1 bg-[#1a1a24] border-white/10 text-white"
                      data-testid="font-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400">Font Family (CSS)</Label>
                    <Input
                      value={newFontFamily}
                      onChange={(e) => setNewFontFamily(e.target.value)}
                      placeholder="MyCustomFont"
                      className="mt-1 bg-[#1a1a24] border-white/10 text-white"
                      data-testid="font-family-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Used in CSS font-family property</p>
                  </div>
                </div>
                
                <div>
                  <Label className="text-gray-400">Font File</Label>
                  <div className="mt-1 flex items-center gap-4">
                    <label className="flex-1 cursor-pointer">
                      <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                        selectedFile 
                          ? 'border-[#10B981] bg-[#10B981]/10' 
                          : 'border-white/10 hover:border-white/20 bg-[#1a1a24]'
                      }`}>
                        {selectedFile ? (
                          <div className="flex items-center justify-center gap-2 text-[#10B981]">
                            <FileType className="w-5 h-5" />
                            <span>{selectedFile.name}</span>
                          </div>
                        ) : (
                          <div className="text-gray-500">
                            <FileType className="w-8 h-8 mx-auto mb-2" />
                            <span>Click to select a font file</span>
                            <p className="text-xs mt-1">.ttf, .otf, .woff, .woff2 (max 5MB)</p>
                          </div>
                        )}
                      </div>
                      <input
                        type="file"
                        accept=".ttf,.otf,.woff,.woff2"
                        onChange={handleFileSelect}
                        className="hidden"
                        data-testid="font-file-input"
                      />
                    </label>
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={uploading || !selectedFile}
                  className="bg-[#10B981] hover:bg-[#059669] text-white"
                  data-testid="upload-font-button"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Upload Font
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Font List */}
          <div>
            <h2 className="text-lg font-semibold text-white mb-4">
              Your Fonts ({fonts.length})
            </h2>
            
            {fonts.length === 0 ? (
              <Card className="bg-[#12121a] border-white/5">
                <CardContent className="p-8 text-center">
                  <Type className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No custom fonts uploaded yet</p>
                  <p className="text-sm text-gray-500 mt-1">Upload your first font above to get started</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {fonts.map((font) => (
                  <Card key={font.id} className="bg-[#12121a] border-white/5">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-[#1a1a24] flex items-center justify-center">
                          <Type className="w-6 h-6 text-[#10B981]" />
                        </div>
                        <div>
                          <h3 className="text-white font-medium">{font.name}</h3>
                          <p className="text-sm text-gray-500">
                            Family: <code className="text-gray-400">{font.font_family}</code>
                          </p>
                        </div>
                      </div>
                      
                      {/* Preview text with the font */}
                      <div className="flex-1 text-center px-4">
                        <style>
                          {`
                            @font-face {
                              font-family: '${font.font_family}';
                              src: url('${font.font_data}');
                            }
                          `}
                        </style>
                        <span 
                          style={{ fontFamily: `'${font.font_family}', sans-serif` }}
                          className="text-xl text-white"
                        >
                          The quick brown fox
                        </span>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(font.id)}
                        className="text-gray-400 hover:text-red-400 hover:bg-red-400/10"
                        data-testid={`delete-font-${font.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#12121a] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Font</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Are you sure you want to delete this font? This action cannot be undone.
              Banners using this font will fall back to the default font.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/5">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
}
